import type { LeftBarTab } from "@v1/reducers/ui-reducer";
import type { ReploElementVersionRevision } from "schemas/generated/element";

import * as React from "react";

import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import AssetsLibraryPane from "@v1/components/assets/AssetsLibraryPane";
import {
  PANEL_CONTENT_TOP_OFFSET,
  PANEL_PADDING,
} from "@v1/components/canvas/canvas-constants";
import BrandDetailsPane from "@v1/components/designLibrary/BrandDetailsPane";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import {
  DEBUG_PANEL_WIDTH,
  MINIMUM_LEFT_BAR_WIDTH,
} from "@v1/components/editor/constants";
import { elementTypeToEditorData } from "@v1/components/editor/element";
import ComponentsPane from "@v1/components/left-bar/ComponentsPane";
import { ComponentTreePane } from "@v1/components/left-bar/ComponentTreePane";
import ElementsPane from "@v1/components/left-bar/ElementsPane";
import SectionsPane from "@v1/components/left-bar/sections-pane/SectionsPane";
import UpdateElementPane from "@v1/components/left-bar/UpdateElementPane";
import ResizablePane from "@v1/components/ResizablePane";
import VersionHistoryPane, {
  VersionHistoryFilter,
} from "@v1/components/version-history/VersionHistoryPaneWrapper";
import { useEditorPerformanceContext } from "@v1/contexts/editor-performance.context";
import { useDraftElementMetadata } from "@v1/hooks/useDraftElementMetadata";
import useElementVersioning from "@v1/hooks/useElementVersioning";
import { setEditorMode } from "@v1/reducers/core-reducer";
import {
  selectLeftBarActiveTab,
  selectLeftBarNavigationOpenedMenu,
  selectLeftBarWidth,
  setLeftBarActiveTab,
  setLeftBarWidth,
} from "@v1/reducers/ui-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import {
  useAdminPanelActions,
  useAdminPanelStore,
} from "@v2/features/admin/adminPanelStore";
import AnnouncementsMenu from "@v2/features/announcements/AnnouncementsMenu";
import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import { useSubscriptionInfo } from "@v2/hooks/subscription";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { useModal } from "@v2/store/modalStore";
import { docs } from "@v2/utils/docs";
import {
  AlignVerticalSpaceAround,
  Archive,
  ChevronLeft,
  CircleHelp,
  File,
  Image,
  Info,
  Layers2,
  LayoutTemplate,
  Plus,
  Store,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

type LeftPanelMenuTab = {
  id: LeftBarTab;
  icon: React.ReactNode;
  activeIcon: React.ReactNode;
  label?: React.ReactNode;
  type: "page" | "project";
  onClick?: () => void;
};

const tabToTitleMap = (displayName: string): Record<LeftBarTab, string> => ({
  tree: "Layers",
  elements: "All Pages",
  components: "Components",
  sections: "Sections",
  shopStyles: "Shop Details",
  assets: "Assets",
  settings: `${displayName} Settings`,
  versionHistory: "Version History",
});

const LeftPanelMenu: React.FC<{
  showElementSpecificContent: boolean;
}> = ({ showElementSpecificContent }) => {
  const dispatch = useEditorDispatch();
  const isDebugMode = useIsDebugMode();
  const modal = useModal();
  const logEvent = useLogAnalytics();
  const { subscriptionInfo } = useSubscriptionInfo();
  const subscriptionTier = subscriptionInfo?.tier ?? "free";
  const isDebugPanelVisible = useAdminPanelStore((s) => s.isVisible);
  const { toggle: toggleDebugPanel } = useAdminPanelActions();
  const navigate = useNavigate();
  const leftBarActiveTab = useEditorSelector(selectLeftBarActiveTab);
  const isGivenTabActive = (tab: LeftBarTab) => leftBarActiveTab === tab;
  const projectId = useCurrentProjectId();

  const tabs: Array<LeftPanelMenuTab> = [
    {
      id: "sections",
      icon: <AlignVerticalSpaceAround size={20} />,
      activeIcon: <AlignVerticalSpaceAround size={20} />,
      label: "Sections",
      type: "page",
    },
    {
      id: "components",
      icon: <Plus size={20} />,
      activeIcon: <Plus size={20} />,
      label: "Components",
      type: "page",
    },
    {
      id: "tree",
      icon: <Layers2 size={20} />,
      activeIcon: <Layers2 size={20} />,
      label: "Layers",
      type: "page",
    },
  ];

  const projectTabs: Array<LeftPanelMenuTab> = [
    {
      id: "elements",
      icon: <File size={20} />,
      activeIcon: <File size={20} />,
      label: "All Pages",
      type: "project",
    },
    {
      id: "assets",
      icon: <Image size={20} />,
      activeIcon: <Image size={20} />,
      label: "Assets",
      type: "project",
    },
    {
      id: "shopStyles",
      icon: <Store size={20} />,
      activeIcon: <Store size={20} />,
      label: "Shop Details",
      type: "project",
    },
  ];

  const renderLeftPanelMenuButton = (
    tab: LeftPanelMenuTab,
    isActive: boolean,
    title: string,
    onClick: () => void,
  ) => (
    <LeftPanelMenuButton
      key={tab.id}
      label={tab.label}
      ariaLabel={title}
      isActive={isActive}
      onClick={onClick}
    >
      {isActive ? tab.activeIcon : tab.icon}
    </LeftPanelMenuButton>
  );

  return (
    <div
      className={twMerge(
        "border-0.5 border-border flex h-full flex-col justify-between gap-3 rounded-l-xl bg-white p-3",
        !leftBarActiveTab && "rounded-xl",
      )}
    >
      <div className="flex h-full flex-col gap-3">
        {showElementSpecificContent && (
          <>
            {tabs.map((tab) => {
              const isActive = isGivenTabActive(tab.id);
              const title = tabToTitleMap("Page")[tab.id];
              return renderLeftPanelMenuButton(tab, isActive, title, () => {
                logEvent("editor.panel.toggle", {
                  toggle: tab.id,
                  isOpened: !isActive,
                  openedWithShortcut: false,
                });
                dispatch(setLeftBarActiveTab(!isActive ? tab.id : null));
              });
            })}
            <div className="border-border border-t-[0.5px]" />
          </>
        )}
        {projectTabs.map((tab) => {
          const isActive = isGivenTabActive(tab.id);
          const title = tabToTitleMap("Page")[tab.id];
          return renderLeftPanelMenuButton(tab, isActive, title, () => {
            logEvent("editor.panel.toggle", {
              toggle: tab.id,
              isOpened: !isActive,
              openedWithShortcut: false,
            });
            dispatch(setLeftBarActiveTab(!isActive ? tab.id : null));
          });
        })}
      </div>
      <div className="flex flex-col gap-3">
        {isFeatureEnabled("archived-pages") && (
          <LeftPanelMenuButton
            label="Archive"
            ariaLabel="Archive"
            onClick={() => {
              dispatch(setEditorMode("archived"));
            }}
          >
            <Archive size={20} className="text-muted" />
          </LeftPanelMenuButton>
        )}
        {isDebugMode && (
          <LeftPanelMenuButton
            label="Debug Menu"
            ariaLabel="Replo Debug Menu"
            isActive={isDebugPanelVisible}
            onClick={toggleDebugPanel}
          >
            <span role="img" aria-label="Debug">
              🔮
            </span>
          </LeftPanelMenuButton>
        )}
        <LeftPanelMenuButton
          label="Browse Templates"
          ariaLabel="Browse Templates"
          onClick={() => {
            logEvent("editor.marketplace.browse", {
              from: "leftBar",
            });
            navigate(`/editor/${projectId}/new`);
            dispatch(setLeftBarActiveTab(null));
          }}
          isActive={window.location.pathname === `/editor/${projectId}/new`}
        >
          <LayoutTemplate
            size={20}
            className={twMerge(
              window.location.pathname === `/editor/${projectId}/new`
                ? ""
                : "text-muted",
            )}
          />
        </LeftPanelMenuButton>

        <AnnouncementsMenu source="editor" />
        <LeftPanelMenuButton
          label="Get Help"
          ariaLabel="Get Help"
          onClick={() => {
            logEvent("user.help.requested", {
              billingPlan: subscriptionTier,
              from: "button",
            });
            modal.openModal({
              type: "supportModal",
            });
          }}
        >
          <CircleHelp className="text-muted" size={20} />
        </LeftPanelMenuButton>
      </div>
    </div>
  );
};

const LeftPanelMenuButton: React.FC<
  React.PropsWithChildren<{
    label: React.ReactNode;
    ariaLabel: string;
    onClick: () => void;
    isActive?: boolean;
  }>
> = ({ label, isActive, ariaLabel, children, onClick }) => {
  return (
    <Tooltip content={label} triggerAsChild>
      <DeprecatedIconButton
        variant="secondary"
        UNSAFE_className={twMerge(
          "shadow-[0px_0px_4px_2px_rgba(0,0,0,0.02)] rounded-md bg-white",
          isActive
            ? "bg-default text-white hover:bg-default"
            : "hover:bg-sheet text-muted hover:text-default border-border border-0.5",
        )}
        aria-label={ariaLabel}
        onClick={onClick}
        icon={children}
      />
    </Tooltip>
  );
};

const LeftPanelContent: React.FC = () => {
  const dispatch = useEditorDispatch();
  const logEvent = useLogAnalytics();
  const leftBarActiveTab = useEditorSelector(selectLeftBarActiveTab);
  // NOTE (Bobby, 2025-03-13) Had to move this hook up here because we wanted to use the version history
  // filter to the top bar. Because of how the left panel is currently built, the panel title and panel content
  // are separate components and since this hook manages local states, we needed to lift the state up so
  // the version history filter could be used in the top bar.
  const {
    versions,
    filterBy,
    setFilterBy,
    isLoading,
    isFetchingNextPage,
    loadMoreRef,
  } = useElementVersioning({
    shouldFetchRevisions: leftBarActiveTab === "versionHistory",
  });

  const draftElementMetadata = useDraftElementMetadata();

  const displayName =
    elementTypeToEditorData[draftElementMetadata?.type ?? "page"]
      ?.singularDisplayName ?? "Page";

  if (leftBarActiveTab === null) {
    return null;
  }

  const content = exhaustiveSwitch({ type: leftBarActiveTab })<{
    component: React.ReactNode;
    documentationLink: string | null;
  }>({
    sections: () => ({
      documentationLink: null,
      component: <SectionsPane />,
    }),
    components: () => ({
      documentationLink: docs.leftBar.insert,
      component: <ComponentsPane />,
    }),
    elements: () => ({
      documentationLink: docs.leftBar.elements,
      component: <ElementsPane />,
    }),
    tree: () => ({
      // TODO (Fran 2024-09-20): Add this documentation link
      documentationLink: null,
      component: <ComponentTreePane />,
    }),
    shopStyles: () => ({
      documentationLink: docs.savedStyles.main,
      component: <BrandDetailsPane />,
    }),
    assets: () => ({
      // TODO (Sebas, 2025-01-31, REPL-15763): Add documentation link
      documentationLink: null,
      component: <AssetsLibraryPane />,
    }),
    settings: () => ({
      documentationLink: null,
      component: <UpdateElementPane />,
    }),
    versionHistory: () => ({
      documentationLink: null,
      component: (
        <VersionHistoryPaneWrapperWithMode
          versions={versions}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          loadMoreRef={loadMoreRef}
        />
      ),
    }),
  });

  return (
    <ResizableLeftPanelContentWrapper>
      <div className="flex h-full flex-col gap-2 overflow-hidden">
        <div className="flex items-center justify-between px-3 pt-3">
          <div className="flex items-center gap-1">
            <span
              className="header-h6 text-default"
              data-testid="left-bar-panel-title"
            >
              {tabToTitleMap(displayName)[leftBarActiveTab]}
            </span>
            {content.documentationLink && (
              <Info
                size={12}
                className="text-muted cursor-pointer"
                onClick={() => {
                  window.open(content.documentationLink ?? "", "_blank");
                }}
              />
            )}
          </div>

          <div className="flex items-center gap-1">
            {leftBarActiveTab === "versionHistory" && (
              <VersionHistoryFilter
                filterBy={filterBy}
                setFilterBy={setFilterBy}
              />
            )}
            <DeprecatedIconButton
              variant="tertiary"
              icon={<ChevronLeft size={12} />}
              onClick={() => {
                logEvent("editor.panel.toggle", {
                  toggle: leftBarActiveTab,
                  isOpened: false,
                  openedWithShortcut: false,
                });
                dispatch(setLeftBarActiveTab(null));
              }}
              aria-label="Close left bar panel"
              size="sm"
            />
          </div>
        </div>
        {content.component}
      </div>
    </ResizableLeftPanelContentWrapper>
  );
};

const ResizableLeftPanelContentWrapper: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const dispatch = useEditorDispatch();
  const logEvent = useLogAnalytics();
  const leftBarWidth = useEditorSelector(selectLeftBarWidth);
  const leftBarNavigationOpenedMenu = useEditorSelector(
    selectLeftBarNavigationOpenedMenu,
  );
  const activeTab = useEditorSelector(selectLeftBarActiveTab);
  const previousResizableWidth = React.useRef(leftBarWidth);

  const { leftBarElementRef } = useEditorPerformanceContext();

  const isLeftBarNavigationOpen = leftBarNavigationOpenedMenu !== "";

  return (
    <div className="flex" ref={leftBarElementRef}>
      <ResizablePane
        minSize={MINIMUM_LEFT_BAR_WIDTH}
        maxSize={500}
        handleClassName="w-[1px] h-[95%] my-2"
        className={twMerge(
          "flex rounded-r-xl",
          isLeftBarNavigationOpen && "rounded-r-none",
        )}
        contentClassName={twMerge(
          "bg-white border-0.5 border-border border-l-0 rounded-r-xl",
          isLeftBarNavigationOpen && "rounded-r-none",
        )}
        size={leftBarWidth}
        onResize={(size) => dispatch(setLeftBarWidth(size))}
        onResizeStop={() => {
          logEvent("editor.panel.resize", {
            previousSize: previousResizableWidth.current,
            newSize: leftBarWidth,
            activePanel: activeTab,
          });
          previousResizableWidth.current = leftBarWidth;
        }}
        isDisabled={isLeftBarNavigationOpen}
      >
        {children}
      </ResizablePane>
    </div>
  );
};

const VersionHistoryPaneWrapperWithMode = ({
  versions,
  isLoading,
  isFetchingNextPage,
  loadMoreRef,
}: {
  versions: ReploElementVersionRevision[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  loadMoreRef: (node: HTMLElement | null) => void;
}) => {
  const dispatch = useEditorDispatch();

  React.useEffect(() => {
    dispatch(setEditorMode("versioning"));
    return () => {
      dispatch(setEditorMode("edit"));
    };
  }, [dispatch]);

  return (
    <VersionHistoryPane
      versions={versions}
      isLoading={isLoading}
      isFetchingNextPage={isFetchingNextPage}
      loadMoreRef={loadMoreRef}
    />
  );
};

const LeftBarPanel = ({
  showElementSpecificContent,
}: {
  showElementSpecificContent: boolean;
}) => {
  const isDebugPanelVisible = useAdminPanelStore((s) => s.isVisible);
  const leftBarActiveTab = useEditorSelector(selectLeftBarActiveTab);
  const isLeftBarClosed = leftBarActiveTab === null;

  return (
    <div
      className={twMerge(
        "shadow-xs absolute flex w-fit rounded-xl",
        isLeftBarClosed && "shadow-none",
      )}
      style={{
        left: isDebugPanelVisible
          ? `${DEBUG_PANEL_WIDTH + PANEL_PADDING}px`
          : PANEL_PADDING,
        top: `${PANEL_CONTENT_TOP_OFFSET + PANEL_PADDING}px`,
        height: `calc(100vh - ${PANEL_CONTENT_TOP_OFFSET + PANEL_PADDING * 2}px)`,
      }}
    >
      <div
        className={twMerge(
          isLeftBarClosed && "shadow-xs rounded-xl",
          !isLeftBarClosed && "rounded-none",
        )}
      >
        <LeftPanelMenu
          showElementSpecificContent={showElementSpecificContent}
        />
      </div>

      <LeftPanelContent />
    </div>
  );
};

export default LeftBarPanel;
